import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';

export class EnhancedCodeValidation {
  static async validateStudentCode(code: string): Promise<{
    isValid: boolean;
    studentData?: any;
    source?: string;
    error?: string;
    details?: any;
  }> {
    try {
      const upperCode = code.trim().toUpperCase();
      const linkingCodeResult = await this.checkInLinkingCodes(upperCode);

      if (linkingCodeResult.found && linkingCodeResult.userId) {
        const studentCheck = await this.checkStudentExists(linkingCodeResult.userId);
        if (studentCheck.exists) {
          return {
            isValid: true,
            studentData: studentCheck.studentData,
            source: 'linkingCodes + users',
            details: {
              linkingCodeData: linkingCodeResult.data,
              studentData: studentCheck.studentData,
            },
          };
        }

        return {
          isValid: false,
          error: 'الكود موجود لكن حساب الطالب غير موجود في users',
          details: {
            linkingCodeExists: true,
            studentExists: false,
            linkingCodeData: linkingCodeResult.data,
          },
        };
      }

      const userResult = await this.checkInUsersCollection(upperCode);
      if (userResult.found) {
        return {
          isValid: true,
          studentData: userResult.data,
          source: 'users',
          details: {
            userData: userResult.data,
          },
        };
      }

      return {
        isValid: false,
        error: 'الكود غير موجود في النظام',
      };
    } catch (error) {
      console.error('Error validating student code:', error);
      return {
        isValid: false,
        error: 'خطأ في التحقق من الكود',
      };
    }
  }

  private static async checkInLinkingCodes(code: string): Promise<{
    found: boolean;
    data?: any;
    userId?: string;
  }> {
    try {
      const codeDocument = await getDoc(doc(db, 'linkingCodes', code));
      if (codeDocument.exists()) {
        const data = codeDocument.data();
        if (data.userType === 'student' && data.isActive !== false) {
          return {
            found: true,
            data,
            userId: data.userId,
          };
        }
      }

      return { found: false };
    } catch (error) {
      console.error('Error checking linkingCodes:', error);
      return { found: false };
    }
  }

  private static async checkStudentExists(userId: string): Promise<{
    exists: boolean;
    studentData?: any;
  }> {
    try {
      const userDocument = await getDoc(doc(db, 'users', userId));
      if (userDocument.exists() && userDocument.data()?.role === 'student') {
        return {
          exists: true,
          studentData: { id: userDocument.id, ...userDocument.data() },
        };
      }

      return { exists: false };
    } catch (error) {
      console.error('Error checking student user:', error);
      return { exists: false };
    }
  }

  private static async checkInUsersCollection(code: string): Promise<{
    found: boolean;
    data?: any;
  }> {
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'student')
      );

      const usersSnapshot = await getDocs(usersQuery);
      for (const userDocument of usersSnapshot.docs) {
        const userData = userDocument.data();
        const studentProfile = userData.studentProfile;

        if (
          userData.linkingCode === code ||
          studentProfile?.studentCode === code ||
          studentProfile?.code === code
        ) {
          return {
            found: true,
            data: {
              id: userDocument.id,
              ...userData,
              studentCode:
                userData.linkingCode ||
                studentProfile?.studentCode ||
                studentProfile?.code,
            },
          };
        }
      }

      return { found: false };
    } catch (error) {
      console.error('Error checking users:', error);
      return { found: false };
    }
  }

  static async fixMissingStudentCode(
    code: string,
    studentData: any
  ): Promise<{
    success: boolean;
    message?: string;
  }> {
    void code;
    void studentData;
    return {
      success: false,
      message:
        'تم إيقاف إصلاح الأكواد بالكتابة في students العامة. أعد توليد كود ربط جديد من حساب الطالب.',
    };
  }
}

export default EnhancedCodeValidation;
